import type { FlowGraph, FlowNode } from '@cartly/shared';
import {
  Background,
  BackgroundVariant,
  Controls,
  Handle,
  MarkerType,
  MiniMap,
  Position,
  ReactFlow,
  type Connection,
  type Edge,
  type Node,
  type NodeProps,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Zap } from 'lucide-react';
import { useMemo, useState } from 'react';
import { slotsOf, slotTarget, type Slot } from './graph';
import { NODE_INFO, summarize, TRIGGER_LABEL } from './labels';
import { layoutFlow, NODE_HEIGHT, NODE_WIDTH } from './layout';

export const TRIGGER_NODE_ID = 'trigger';

interface StepData extends Record<string, unknown> {
  node: FlowNode;
  isStart: boolean;
  hasIssue: boolean;
}
interface TriggerData extends Record<string, unknown> {
  trigger: FlowGraph['trigger'];
}

function StepCard({ data, selected }: NodeProps<Node<StepData>>) {
  const { node, isStart, hasIssue } = data;
  const info = NODE_INFO[node.type];
  const Icon = info.icon;
  const classes = [
    'flow-node',
    selected ? 'flow-node--selected' : '',
    hasIssue ? 'flow-node--issue' : '',
  ];
  return (
    <div className={classes.filter(Boolean).join(' ')}>
      <Handle type="target" position={Position.Top} className="flow-handle" />
      <div className="flow-node__head">
        <Icon size={14} aria-hidden />
        <span className="flow-node__title">{info.title}</span>
        {isStart && <span className="flow-node__badge">Start</span>}
      </div>
      <div className="flow-node__summary">{summarize(node)}</div>
      {node.type === 'split' ? (
        <>
          <div className="flow-node__branches" aria-hidden>
            <span>A {node.percentA}%</span>
            <span>B {100 - node.percentA}%</span>
          </div>
          <Handle
            id="a"
            type="source"
            position={Position.Bottom}
            className="flow-handle flow-handle--yes"
          />
          <Handle
            id="b"
            type="source"
            position={Position.Bottom}
            className="flow-handle flow-handle--no"
          />
        </>
      ) : node.type === 'condition' ? (
        <>
          <div className="flow-node__branches" aria-hidden>
            <span>Yes</span>
            <span>No</span>
          </div>
          <Handle
            id="yes"
            type="source"
            position={Position.Bottom}
            className="flow-handle flow-handle--yes"
          />
          <Handle
            id="no"
            type="source"
            position={Position.Bottom}
            className="flow-handle flow-handle--no"
          />
        </>
      ) : (
        <Handle id="next" type="source" position={Position.Bottom} className="flow-handle" />
      )}
    </div>
  );
}

function TriggerCard({ data, selected }: NodeProps<Node<TriggerData>>) {
  const label = TRIGGER_LABEL[data.trigger];
  return (
    <div className={`flow-node flow-node--trigger${selected ? ' flow-node--selected' : ''}`}>
      <div className="flow-node__head">
        <Zap size={14} aria-hidden />
        <span className="flow-node__title">When</span>
      </div>
      <div className="flow-node__summary">{label.title}</div>
      <Handle id="next" type="source" position={Position.Bottom} className="flow-handle" />
    </div>
  );
}

const nodeTypes = { step: StepCard, trigger: TriggerCard };

export function FlowCanvas({
  graph,
  selectedId,
  issueNodeIds,
  editable,
  onSelect,
  onLink,
  onDeleteNode,
}: {
  graph: FlowGraph;
  selectedId: string | null;
  issueNodeIds: Set<string>;
  editable: boolean;
  onSelect: (id: string | null) => void;
  /** `from` is TRIGGER_NODE_ID when the start is being set. */
  onLink: (from: string, slot: Slot, to: string | null) => void;
  onDeleteNode: (id: string) => void;
}) {
  // Edges are controlled too, so their selection (needed for Delete) is tracked here.
  const [selectedEdge, setSelectedEdge] = useState<string | null>(null);
  const { nodes, edges } = useMemo(() => {
    const positions = layoutFlow(graph);
    const nodes: Node[] = [
      {
        id: TRIGGER_NODE_ID,
        type: 'trigger',
        position: { x: -NODE_WIDTH / 2, y: 0 },
        data: { trigger: graph.trigger } satisfies TriggerData,
        selected: selectedId === TRIGGER_NODE_ID,
        draggable: false,
        deletable: false,
      },
      ...graph.nodes.map((n): Node => {
        const p = positions.get(n.id) ?? { x: 0, y: NODE_HEIGHT * 2 };
        return {
          id: n.id,
          type: 'step',
          position: { x: p.x - NODE_WIDTH / 2, y: p.y },
          data: {
            node: n,
            isStart: graph.start === n.id,
            hasIssue: issueNodeIds.has(n.id),
          } satisfies StepData,
          selected: selectedId === n.id,
          draggable: false,
        };
      }),
    ];
    const edges: Edge[] = [];
    const arrow = {
      type: MarkerType.ArrowClosed,
      width: 16,
      height: 16,
      color: 'var(--color-text-subtle)',
    };
    if (graph.start !== null) {
      edges.push({
        id: `${TRIGGER_NODE_ID}->${graph.start}`,
        source: TRIGGER_NODE_ID,
        sourceHandle: 'next',
        target: graph.start,
        markerEnd: arrow,
        deletable: false,
      });
    }
    for (const n of graph.nodes) {
      for (const slot of slotsOf(n)) {
        const to = slotTarget(n, slot);
        if (to === null) continue;
        edges.push({
          id: `${n.id}:${slot}->${to}`,
          source: n.id,
          sourceHandle: slot,
          target: to,
          markerEnd: arrow,
          selected: selectedEdge === `${n.id}:${slot}->${to}`,
          ...(n.type === 'condition' ? { label: slot === 'yes' ? 'Yes' : 'No' } : {}),
          ...(n.type === 'split' ? { label: slot === 'a' ? 'A' : 'B' } : {}),
        });
      }
    }
    return { nodes, edges };
  }, [graph, selectedId, issueNodeIds, selectedEdge]);

  const onConnect = (c: Connection) => {
    if (!editable || !c.source || !c.target || c.target === TRIGGER_NODE_ID) return;
    if (c.source === c.target) return;
    onLink(c.source, (c.sourceHandle as Slot | null) ?? 'next', c.target);
  };

  return (
    <div className="flow-canvas">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        nodesDraggable={false}
        nodesConnectable={editable}
        elementsSelectable
        deleteKeyCode={editable ? ['Backspace', 'Delete'] : null}
        onNodeClick={(_, n) => {
          setSelectedEdge(null);
          onSelect(n.id);
        }}
        onEdgeClick={(_, e) => {
          setSelectedEdge(e.id);
          onSelect(null);
        }}
        onPaneClick={() => {
          setSelectedEdge(null);
          onSelect(null);
        }}
        onConnect={onConnect}
        onEdgesDelete={(deleted) => {
          setSelectedEdge(null);
          if (!editable) return;
          for (const e of deleted) {
            if (e.source === TRIGGER_NODE_ID) continue;
            onLink(e.source, (e.sourceHandle as Slot | null) ?? 'next', null);
          }
        }}
        onNodesDelete={(deleted) => {
          if (!editable) return;
          for (const n of deleted) if (n.id !== TRIGGER_NODE_ID) onDeleteNode(n.id);
        }}
        fitView
        fitViewOptions={{ padding: 0.25, maxZoom: 1 }}
        minZoom={0.3}
        maxZoom={1.5}
        proOptions={{ hideAttribution: true }}
        aria-label="Flow canvas"
      >
        <Background variant={BackgroundVariant.Dots} gap={20} size={1} />
        <Controls showInteractive={false} />
        <MiniMap pannable zoomable ariaLabel="Flow overview" nodeStrokeWidth={2} />
      </ReactFlow>
    </div>
  );
}
